import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { QualityReport } from '../../types/quality';
import { qualityTemplates } from '../../data/qualityTemplates';
import { company } from '../../data/company';
import { Customer } from '../CustomerCard';
interface NewQualityReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (report: Omit<QualityReport, 'id'>) => void;
  customers: Customer[];
  nextReportId: string;
  responsible: {
    name: string;
    email: string;
    phone: string;
  };
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function NewQualityReportModal({
  isOpen,
  onClose,
  onCreate,
  customers,
  nextReportId,
  responsible
}: NewQualityReportModalProps) {
  const [title, setTitle] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [manualCustomer, setManualCustomer] = useState('');
  const [templateId, setTemplateId] = useState(qualityTemplates[0].id);
  const reset = () => {
    setTitle('');
    setCustomerId('');
    setManualCustomer('');
    setTemplateId(qualityTemplates[0].id);
  };
  const selectedCustomer = customers.find(c => c.id === customerId);
  const customerName = selectedCustomer?.name || manualCustomer.trim();
  const canSubmit = title.trim().length > 0 && customerName.length > 0;
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    const template = qualityTemplates.find(t => t.id === templateId) ?? qualityTemplates[0];
    onCreate({
      reportId: nextReportId,
      title: title.trim(),
      status: 'Åben',
      visibleToCustomer: false,
      description: '',
      customer: {
        name: customerName,
        address: (selectedCustomer as any)?.address || ''
      },
      quote: {
        reference: '',
        status: 'Ikke tilknyttet',
        amount: 0
      },
      project: {
        reference: '',
        title: title.trim()
      },
      responsible,
      company: {
        ...company
      },
      sections: template.build(),
      photos: []
    });
    reset();
    onClose();
  };
  return <AnimatePresence data-id="element-593" data-mp="17vniobfx83hz">
      {isOpen && <div role="dialog" aria-modal="true" aria-labelledby="new-ks-title" className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-6" data-id="element-594" data-mp="12vrby4fu9xyq">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: EASE
      }} onClick={onClose} className="absolute inset-0 bg-stone-900/40" data-id="element-595" data-mp="u1wgjo4irlwa" />

          <motion.form onSubmit={handleSubmit} initial={{
        opacity: 0,
        y: 24,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 12,
        scale: 0.98
      }} transition={{
        duration: 0.24,
        ease: EASE
      }} className="relative flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:max-w-lg sm:rounded-2xl" data-id="element-596" data-mp="f66phbkh8ikp">
            <header className="flex items-start justify-between gap-4 border-b border-stone-200 px-5 py-4" data-id="element-597" data-mp="3nd3n7mrmphoa">
              <div data-id="element-598" data-mp="3sby24dmh6m34">
                <h2 id="new-ks-title" className="text-lg font-bold tracking-tight text-stone-900" data-id="element-599" data-mp="zvdds3xbe9z">
                  Ny kvalitetssikring
                </h2>
                <p className="mt-0.5 text-sm text-stone-500" data-id="element-600" data-mp="34gvbc04428k0">
                  Får nummer{' '}
                  <span className="font-mono font-medium text-brand-700" data-id="element-601" data-mp="39ewxitwu2nzp">
                    {nextReportId}
                  </span>
                </p>
              </div>
              <button type="button" onClick={onClose} aria-label="Luk" className="-mr-1 rounded-lg p-1.5 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700" data-id="element-602" data-mp="3djczwl78nda6">
                <XIcon className="h-5 w-5" data-id="element-603" data-mp="xokwtmv78ey2" />
              </button>
            </header>

            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5" data-id="element-604" data-mp="w9rua5ifo0l2">
              <label className="block" data-id="element-605" data-mp="raf9rrf1b5ln">
                <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-606" data-mp="rbgme0bp805h">
                  Hvad handler opgaven om?{' '}
                  <span className="text-red-500" data-id="element-607" data-mp="117tfox3c0bzp">*</span>
                </span>
                <input autoFocus value={title} onChange={e => setTitle(e.target.value)} placeholder="f.eks. Asbesttag nedtagning og fjernelse" className="w-full rounded-xl border border-stone-200 px-4 py-2.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500" data-id="element-608" data-mp="119wfu9y77li5" />
              </label>

              <div data-id="element-609" data-mp="1xy2emg8fvjoi">
                <label className="block" data-id="element-610" data-mp="1xxj2jg5gzpie">
                  <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-611" data-mp="1xyc5e61b5uv9">
                    Kunde <span className="text-red-500" data-id="element-612" data-mp="1t0ke9e8hmcs4">*</span>
                  </span>
                  <select value={customerId} onChange={e => setCustomerId(e.target.value)} className="w-full rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500" data-id="element-613" data-mp="2cm2q356aiax3">
                    <option value="" data-id="element-614" data-mp="27nrg8ufzw0ev">Vælg kunde…</option>
                    {customers.map(customer => <option key={customer.id} value={customer.id} data-id="element-615" data-mp="1tsgnpnp83rdo">
                        {customer.customerId} — {customer.name}
                      </option>)}
                  </select>
                </label>
                {!customerId && <input value={manualCustomer} onChange={e => setManualCustomer(e.target.value)} placeholder="…eller skriv kundens navn" className="mt-2 w-full rounded-xl border border-stone-200 px-4 py-2.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500" data-id="element-616" data-mp="1ovq811hbyxss" />}
              </div>

              <fieldset data-id="element-617" data-mp="3cr4ugltuojyd">
                <legend className="mb-2 text-sm font-medium text-stone-700" data-id="element-618" data-mp="37tldvf9ojv39">
                  Start fra skabelon
                </legend>
                <div className="space-y-2" data-id="element-619" data-mp="33tktldzwgpoa">
                  {qualityTemplates.map(template => {
                const isActive = templateId === template.id;
                return <label key={template.id} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors ${isActive ? 'border-brand-300 bg-brand-50' : 'border-stone-200 hover:bg-stone-50'}`} data-id="element-620" data-mp="3vkqsjr3vwhbz">
                        <input type="radio" name="ks-template" value={template.id} checked={isActive} onChange={() => setTemplateId(template.id)} className="mt-0.5 h-4 w-4 flex-shrink-0 accent-brand-600" data-id="element-621" data-mp="2b212z90z49c9" />
                        <span className="min-w-0" data-id="element-622" data-mp="14drm2k5n2ofo">
                          <span className="block text-sm font-semibold text-stone-900" data-id="element-623" data-mp="19c1j70axbz3x">
                            {template.label}
                          </span>
                          <span className="block text-xs text-stone-500" data-id="element-624" data-mp="1e9b3k1jghkkw">
                            {template.description}
                          </span>
                        </span>
                      </label>;
              })}
                </div>
              </fieldset>
            </div>

            <footer className="flex gap-3 border-t border-stone-200 px-5 py-4" data-id="element-625" data-mp="3l3yety2sbt3y">
              <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-stone-200 px-4 py-2.5 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-50" data-id="element-626" data-mp="3p3h0wx6cea8d">
                Annuller
              </button>
              <button type="submit" disabled={!canSubmit} className="flex-1 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-stone-200 disabled:text-stone-400" data-id="element-627" data-mp="3eyh00e7x1omb">
                Opret
              </button>
            </footer>
          </motion.form>
        </div>}
    </AnimatePresence>;
}